import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { IonIcon, useIonRouter } from '@ionic/react';
import { barChartOutline, bookOutline, callOutline, keypadOutline, micCircleOutline, peopleCircleOutline, personOutline } from 'ionicons/icons';
import { menuItems, robotOutline } from '../../config/menuItems';
import { useRefresh } from '../../contexts/RefreshContext';
import revLogo from '../../assets/REV_logo.svg';

const ionIconMap: Record<string, string> = {
  barChartOutline,
  bookOutline,
  callOutline,
  peopleCircleOutline,
  personOutline,
  robotOutline,
  keypadOutline,
  micCircleOutline,
};

interface DesktopSidebarProps {
  isOpen: boolean;
}

export function DesktopSidebar({ isOpen }: DesktopSidebarProps) {
  const location = useLocation();
  const router = useIonRouter();
  const { triggerRefresh } = useRefresh();

  // Filter menu items based on shouldShow
  const visibleMenuItems = menuItems.filter((item) => {
    if (!item.shouldShow) return true;
    return item.shouldShow();
  });

  return (
    <aside
      className={`fixed left-0 top-0 h-screen bg-white border-r border-gray-200 transition-all duration-300 ease-in-out z-40 flex flex-col overflow-hidden ${isOpen ? 'w-64' : 'w-20'
        }`}
    >
      <div className="flex-1 overflow-y-auto overflow-x-hidden">
        {/* Logo/Brand */}
        <div className="flex items-center px-4 py-4">
          <Link to="/dashboard" className="flex items-center">
            <img src={revLogo} alt="REV Logo" className="h-8 w-auto dark:hidden" />
            <img src={revLogo} alt="REV Logo" className="h-8 w-auto hidden dark:block" />
          </Link>
        </div>

        <nav className="flex flex-col py-4">
          {visibleMenuItems.map((item, index) => {
            const isActive = location.pathname === item.path || location.pathname.startsWith(item.path + '/');
            const isMain = item.isMain === true;
            const prevItem = index > 0 ? visibleMenuItems[index - 1] : null;
            const showDivider = prevItem && prevItem.isMain && !isMain;

            return (
              <React.Fragment key={item.path}>
                {showDivider && (
                  <div className="mx-2 my-2 border-t border-gray-200" />
                )}
                <button
                  onClick={() => { router.push(item.path, 'none'); triggerRefresh(item.path); }}
                  className={`flex items-center px-4 py-3 mx-2 rounded-lg transition-colors group relative ${isActive
                    ? 'nav-menu-item-active'
                    : 'nav-menu-item'
                    }`}
                  title={!isOpen ? item.label : undefined}
                >
                  <div className={`flex items-center justify-center min-w-[24px] ${isOpen ? 'mr-3' : 'mx-auto'}`}>
                    {item.ionIcon && ionIconMap[item.ionIcon] ? (
                      <IonIcon icon={ionIconMap[item.ionIcon]} className="text-2xl" />
                    ) : item.icon ? (
                      <img src={item.icon} alt={item.label} className={`w-6 h-6 ${!isMain ? 'opacity-90' : ''}`} />
                    ) : null}
                  </div>

                  <span
                    className={`whitespace-nowrap transition-opacity duration-300 ${isOpen ? 'opacity-100' : 'opacity-0 w-0 overflow-hidden'
                      } ${!isMain ? 'text-gray-600' : ''}`}
                  >
                    {item.label}
                  </span>
                </button>
              </React.Fragment>
            );
          })}
        </nav>
      </div>

      {/* Version Number - Bottom Left Corner */}
      <div className="fixed bottom-2 left-2 text-[8px] text-gray-400">
        {import.meta.env.VITE_VERSION}
      </div>
    </aside>
  );
}
